import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import moment, { Moment } from "moment";
import classNames from "classnames";
import { CloseButton, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import Button from "@/component/Button";
import { warningToast } from "@/component/Toast";
import InViewObserver from "@/component/InViewObserver";

import { DateRange, StrictDateRange } from "./type";
import {
    adjustSameMonth,
    cloneDateRange,
    DATE_FORMAT,
    dateRangeInvalidCause,
    getDateRangeInDayOrder
} from "./helper";
import Calendar from "./Calendar";


type DateRangePickerProps = {
    max: number;
    period: DateRange;
    onChangePeriod: (period: DateRange) => void;
    children?: React.ReactNode;
};

const DateRangePicker: React.FC<DateRangePickerProps> = (props) => {
    const { max, period, onChangePeriod, children } = props;

    const intl = useIntl();

    const [calendarMonths, setCalendarMonths] = useState<{start: Moment, end: Moment}>();
    const [hoveredDate, setHoveredDate] = useState<Moment | undefined>(undefined);
    const [selectedDates, setSelectedDates] = useState<DateRange>(cloneDateRange(period));

    const changeSelectedDates = useCallback((dateRange: DateRange) => {
        const datesClone = cloneDateRange(dateRange);
        datesClone.start = datesClone.start?.clone().startOf('day');
        datesClone.end = datesClone.end?.clone().endOf('day');
        setSelectedDates(datesClone);
    }, []);

    const init = useCallback(() => {
        const initMonths = {
            start: (period.start ?? moment()).clone().startOf('month'),
            end: (period.end ?? moment()).clone().startOf('month')
        };
        setCalendarMonths(adjustSameMonth(initMonths, 'backward'));

        changeSelectedDates(period);
    }, [period, changeSelectedDates]);

    const validate = useCallback((dateRange: DateRange): dateRange is StrictDateRange => {
        return dateRangeInvalidCause(dateRange, max) === undefined;
    }, [max]);

    const warnToast = useCallback((dateRange: DateRange) => {
        switch (dateRangeInvalidCause(dateRange, max)) {
            case "max":
                warningToast(intl.formatMessage({ id: "i00064" }, { max }));
                break;
            default:
                break;
        }
    }, [intl, max]);

    const onClickSubmitBtn = (e: React.MouseEvent) => {
        if (!validate(selectedDates)) {
            e.preventDefault();
            warnToast(selectedDates);
            return;
        }
        onChangePeriod(cloneDateRange(selectedDates));
    };

    const onClickDate = (clickedDate: Moment) => {
        const { start, end } = selectedDates;
        const range =
            (start && !end)
                ? getDateRangeInDayOrder(start, clickedDate)
                : { start: clickedDate.clone(), end: undefined };
        range.start = range.start?.clone().startOf('day');
        range.end = range.end?.clone().endOf('day');
        if (!validate(range)) {
            warnToast(range);
        }
        changeSelectedDates(range);
    };

    const onChangeMonth = (pos: "start" | "end", targetMonth: Moment) => {
        if (!calendarMonths) {
            return;
        }
        const monthsClone = cloneDateRange(calendarMonths);
        monthsClone[pos] = targetMonth.clone().startOf('month');

        setCalendarMonths(adjustSameMonth(monthsClone, pos === 'start' ? 'forward' : 'backward'));
    };

    const onChangeTextInput = (pos: "start" | "end", date: Moment) => {
        const dateClone = date.clone();
        const selectedDatesClone = cloneDateRange(selectedDates);
        selectedDatesClone[pos] = dateClone;

        if (selectedDatesClone.start && selectedDatesClone.end) {
            changeSelectedDates(getDateRangeInDayOrder(selectedDatesClone.start, selectedDatesClone.end));
        } else {
            changeSelectedDates(selectedDatesClone);
        }
    };

    const activeDateRange = useMemo(() => {
        if (!hoveredDate) {
            return selectedDates;
        }
        const { start, end } = cloneDateRange(selectedDates);

        if (!start && !end) {
            return { start: hoveredDate, end: undefined };
        } else if (start && !end) {
            return getDateRangeInDayOrder(start, hoveredDate);
        } else {
            return selectedDates;
        }
    }, [hoveredDate, selectedDates]);

    useEffect(() => {
        init();
    }, [init]);

    if (!calendarMonths) {
        return null;
    }
    return (
        <Popover>{({ open, close }) => (<>
            <InViewObserver enabled={open}
                            onHidden={close}
            >{inViewRef => (
                <PopoverButton as="div"
                               ref={inViewRef}
                               onClick={init}
                >
                    {children}
                </PopoverButton>
            )}</InViewObserver>
            <PopoverPanel transition
                          role="dialog"
                          aria-label={intl.formatMessage({ id: "i00064" }, { max })}
                          anchor={{ to: "bottom start", gap: 8 }}
                          className={classNames(
                              "px-4 pt-3 pb-5 flex flex-col gap-6 bg-white border border-black-200 rounded-[6px] z-[999]",
                              "transition duration-200 ease-in-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
                          )}
            >
                <div className="flex gap-8">
                    <Calendar month={calendarMonths.start}
                              activeDateRange={activeDateRange}
                              onSelect={onClickDate}
                              onHover={setHoveredDate}
                              onChangeMonth={month => onChangeMonth("start", month)}
                    />
                    <Calendar month={calendarMonths.end}
                              activeDateRange={activeDateRange}
                              onSelect={onClickDate}
                              onHover={setHoveredDate}
                              onChangeMonth={month => onChangeMonth("end", month)}
                    />
                </div>
                <div className="flex justify-between items-center">
                    <div className="text-black-400 text-[12px] font-[400]">
                        {intl.formatMessage({ id: "i00064" }, { max })}
                    </div>
                    <div className="flex gap-2">
                        <div className="flex items-center gap-1">
                            <DateInput value={selectedDates.start?.clone()}
                                       aria-label="Start date"
                                       onInputValidDate={date => onChangeTextInput("start", date)}
                            />

                            <div className="w-2 text-chat42 text-[13px] font-[400]">
                                ~
                            </div>

                            <DateInput value={selectedDates.end?.clone()}
                                       aria-label="End date"
                                       onInputValidDate={date => onChangeTextInput("end", date)}
                            />
                        </div>
                        <CloseButton as="div">
                            <Button size="xs"
                                    variant="primary"
                                    className="px-4 py-1 whitespace-nowrap"
                                    disabled={!validate(selectedDates)}
                                    onClick={onClickSubmitBtn}
                            >
                                {intl.formatMessage({ id: "b0001" })}
                            </Button>
                        </CloseButton>
                    </div>
                </div>
            </PopoverPanel>
        </>)}</Popover>
    );
};

interface DateInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value'> {
    value?: Moment;
    onInputValidDate: (date: Moment) => void;
}

const DateInput: React.FC<DateInputProps> = (props) => {
    const { value, onInputValidDate, ...rest } = props;

    const intl = useIntl();

    const inputRef = useRef<HTMLInputElement>(null);
    const [inputText, setInputText] = useState<string>(value?.format(DATE_FORMAT) ?? "");
    const today = useMemo(() => moment().startOf('day'), []);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        if (!input) {
            return;
        }
        const date = moment(input, DATE_FORMAT, true);
        if (!date.isValid()) {
            return;
        }
        if (date.isAfter(today, 'day')) {
            warningToast(intl.formatMessage({ id: "i00074" }));
            return;
        }
        onInputValidDate(date);
    };

    const onBlur = () => {
        setInputText(value?.format(DATE_FORMAT) ?? "");
        if (!moment(inputText, DATE_FORMAT, true).isValid()) {
            warningToast(intl.formatMessage({ id: "i00075" }));
        };
    };

    useEffect(() => {
        if (!inputRef.current || document.activeElement === inputRef.current) {
            return;
        }
        setInputText(value?.format(DATE_FORMAT) ?? "");
    }, [value]);

    return (
        <input ref={inputRef}
               type="text"
               className="input-default w-20 py-1.5 px-2 outline-chat42 text-[13px]"
               value={inputText}
               placeholder="yyyy.mm.dd"
               onChange={onChange}
               onBlur={onBlur}
               {...rest}
        />
    );
};

export default DateRangePicker;
